import { sectionWithHeight } from '@settings/common';

const backgroundImage = () => ({
  label: 'Background Image Settings',
  name: 'backgroundImage',
  description: 'Background images for desktop and mobile',
  component: 'group',
  fields: [
    {
      name: 'desktop',
      label: 'Desktop Background',
      component: 'group',
      fields: [
        {
          name: 'src',
          label: 'Desktop Image',
          component: 'image',
        },
        {
          name: 'position',
          component: 'select',
          label: 'Image position (desktop)',
          options: [
            { label: 'Center Center', value: 'center center' },
            { label: 'Center Top', value: 'center top' },
            { label: 'Center Bottom', value: 'center bottom' },
            { label: 'Left Center', value: 'left center' },
            { label: 'Right Center', value: 'right center' },
          ],
        },
      ],
    },
    {
      name: 'mobile',
      label: 'Mobile Background',
      component: 'group',
      fields: [
        {
          name: 'src',
          label: 'Mobile Image',
          component: 'image',
        },
        {
          name: 'position',
          component: 'select',
          label: 'Image position (mobile)',
          options: [
            { label: 'Center Center', value: 'center center' },
            { label: 'Center Top', value: 'center top' },
            { label: 'Center Bottom', value: 'center bottom' },
            { label: 'Left Center', value: 'left center' },
            { label: 'Right Center', value: 'right center' },
          ],
        },
      ],
    },
  ],
  defaultValue: {
    desktop: {
      src: 'https://cdn.shopify.com/s/files/1/1114/2308/files/HeroBg.png',
      position: 'center center',
    },
    mobile: {
      src: 'https://cdn.shopify.com/s/files/1/1114/2308/files/Screen_Shot_2022-04-27_at_2.33.14_PM.png',
      position: 'center center',
    },
  },
});

const text = () => ({
  label: 'Text Settings',
  name: 'text',
  description: 'Superheading, heading, subheading, text color',
  component: 'group',
  fields: [
    {
      name: 'logo',
      label: 'Logo',
      component: 'image',
      description: 'Recommended image size: 72px x 72px',
    },
    {
      name: 'superheading',
      component: 'text',
      label: 'Superheading',
    },
    {
      name: 'heading',
      component: 'text',
      label: 'Heading',
    },
    {
      name: 'headingOption',
      label: 'Font Family Heading Option',
      component: 'radio-group',
      direction: 'horizontal',
      variant: 'radio',
      options: [
        { label: 'Reckless Neue', value: 'text.h1' },
        { label: 'Fellix', value: 'text.h1.2' },
      ],
      defaultValue: 'text.h1',
    },
    {
      name: 'subheading',
      component: 'text',
      label: 'Subheading / Body',
    },
    {
      name: 'desktopFontSize',
      component: 'select',
      label: 'Desktop Font Size',
      description: 'Font size for desktop devices',
      options: [
        { label: '60px', value: 60 },
        { label: '72px', value: 72 },
        { label: '84px (Default)', value: 84 },
        { label: '96px', value: 96 },
        { label: '108px', value: 108 },
        { label: 'Custom', value: 'custom' },
      ],
      defaultValue: 84,
    },
    {
      name: 'customDesktopFontSize',
      component: 'number',
      label: 'Custom Desktop Font Size (px)',
      description: 'Only used when "Custom" is selected above',
      condition: {
        component: 'desktopFontSize',
        operator: '===',
        value: 'custom',
      },
    },
    {
      name: 'mobileFontSize',
      component: 'select',
      label: 'Mobile Font Size',
      description: 'Font size for mobile devices',
      options: [
        { label: '32px', value: 32 },
        { label: '40px', value: 40 },
        { label: '44px (Default)', value: 44 },
        { label: '48px', value: 48 },
        { label: '56px', value: 56 },
        { label: 'Custom', value: 'custom' },
      ],
      defaultValue: 44,
    },
    {
      name: 'customMobileFontSize',
      component: 'number',
      label: 'Custom Mobile Font Size (px)',
      description: 'Only used when "Custom" is selected above',
      condition: {
        component: 'mobileFontSize',
        operator: '===',
        value: 'custom',
      },
    },
    {
      component: 'select',
      name: 'color',
      label: 'Text color',
      options: [
        { label: 'White', value: '#ffffff' },
        { label: 'Black', value: '#000000' },
        { label: 'Gray', value: '#666666' },
      ],
    },
  ],
  defaultValue: {
    superheading: '',
    heading: 'The only 100% leakproof straw tumbler on the market.',
    subheading: '',
    desktopFontSize: 84,
    customDesktopFontSize: 84,
    mobileFontSize: 44,
    customMobileFontSize: 44,
    color: '#ffffff',
  },
});

const socialProof = () => ({
  label: 'Social Proof Animation',
  name: 'socialProof',
  description: 'Animated social proof with incrementing numbers',
  component: 'group',
  fields: [
    {
      label: 'Enable Social Proof',
      name: 'enabled',
      component: 'toggle',
      toggleLabels: {
        true: 'On',
        false: 'Off',
      },
      defaultValue: true,
    },
    {
      name: 'baseNumber',
      component: 'number',
      label: 'Base Number',
      description: 'Starting number for the animation',
      defaultValue: 30000,
    },
    {
      name: 'label',
      component: 'text',
      label: 'Label',
      description:
        'Text that appears after the number (e.g., "5 Star Reviews")',
      defaultValue: '5 Star Reviews',
    },
    {
      name: 'ringColor',
      component: 'color',
      label: 'Image Ring Color',
      description:
        'Color of the ring/border around user images. Set to match your background for a transparent effect.',
      defaultValue: '#ffffff',
    },
    {
      name: 'items',
      label: 'Social Proof Items',
      component: 'group-list',
      description: 'Max of 10 items',
      itemProps: {
        label: '{{item.image.alt}}',
      },
      validate: {
        maxItems: 10,
      },
      defaultItem: {
        image: {
          src: '',
          alt: 'User',
        },
      },
      fields: [
        {
          name: 'image',
          component: 'group',
          label: 'User Image',
          fields: [
            {
              name: 'src',
              label: 'Image',
              component: 'image',
            },
            {
              name: 'alt',
              component: 'text',
              label: 'Alt Text',
              defaultValue: 'User',
            },
          ],
        },
      ],
      defaultValue: [
        {
          image: {
            src: '',
            alt: 'User 1',
          },
        },
        {
          image: {
            src: '',
            alt: 'User 2',
          },
        },
        {
          image: {
            src: '',
            alt: 'User 3',
          },
        },
        {
          image: {
            src: '',
            alt: 'User 4',
          },
        },
      ],
    },
  ],
});

const valueProps = () => ({
  label: 'Brand Value Props',
  name: 'valueProps',
  description: 'Brand value propositions with icons, titles and descriptions',
  component: 'group',
  fields: [
    {
      label: 'Enable Value Props',
      name: 'enabled',
      component: 'toggle',
      toggleLabels: {
        true: 'On',
        false: 'Off',
      },
      defaultValue: true,
    },
    {
      component: 'color',
      name: 'backgroundColor',
      label: 'Background Color',
      defaultValue: 'rgba(0,0,0,0.8)',
    },
    {
      label: 'Text Color',
      name: 'textColor',
      component: 'color',
      defaultValue: 'rgba(255,255,255,1)',
    },
    {
      name: 'items',
      label: 'Value Props',
      component: 'group-list',
      description: 'Brand value propositions',
      itemProps: {
        label: '{{item.title}}',
      },
      defaultItem: {
        icon: {
          src: '',
          alt: 'Icon',
        },
        title: 'Value Prop',
        description: 'Description of the value proposition',
      },
      fields: [
        {
          name: 'icon',
          component: 'group',
          label: 'Icon',
          fields: [
            {
              name: 'src',
              label: 'Icon Image/SVG',
              component: 'image',
            },
            {
              name: 'alt',
              component: 'text',
              label: 'Alt Text',
              defaultValue: 'Icon',
            },
          ],
        },
        {
          name: 'title',
          component: 'text',
          label: 'Title',
        },
        {
          name: 'description',
          component: 'textarea',
          label: 'Description',
          description: 'Hidden on mobile',
        },
      ],
      defaultValue: [
        {
          icon: {
            src: 'https://cdn.shopify.com/s/files/1/1114/2308/files/check-icon.svg',
            alt: 'Check icon',
          },
          title: 'Innovative Design',
          description:
            'Our innovative design, has won 3 prestigious Red Dot Design Awards.',
        },
        {
          icon: {
            src: 'https://cdn.shopify.com/s/files/1/1114/2308/files/warranty-icon.svg',
            alt: 'Warranty icon',
          },
          title: 'Lifetime Warranty',
          description:
            'Our limited lifetime warranty ensures lasting quality & peace of mind.',
        },
        {
          icon: {
            src: 'https://cdn.shopify.com/s/files/1/1114/2308/files/service-icon.svg',
            alt: 'Service icon',
          },
          title: 'Flawless Customer Service',
          description:
            'The BrüGurus are dedicated to providing exceptional support via email and chat.',
        },
        {
          icon: {
            src: 'https://cdn.shopify.com/s/files/1/1114/2308/files/shipping-icon.svg',
            alt: 'Shipping icon',
          },
          title: 'Free Shipping on Us Order $75+',
          description:
            'Free shipping on orders over $75 so you can enjoy premium drinkware without added delivery costs.',
        },
      ],
    },
  ],
});

const section = ({ sectionWithHeight: sectionConfig }) => ({
  ...sectionConfig,
  fields: [
    {
      label: 'Above the fold',
      name: 'aboveTheFold',
      component: 'toggle',
      toggleLabels: {
        true: 'Yes',
        false: 'No',
      },
      defaultValue: true,
    },
    ...sectionConfig.fields,
    {
      label: 'Hero Padding',
      name: 'heroPadding',
      description: 'Control padding inside the hero component',
      component: 'group',
      fields: [
        {
          name: 'desktop',
          label: 'Desktop Padding',
          component: 'group',
          fields: [
            {
              name: 'py',
              label: 'Vertical Padding',
              component: 'select',
              options: [
                { label: 'None', value: 'none' },
                { label: 'Small', value: 's' },
                { label: 'Medium', value: 'm' },
                { label: 'Large', value: 'l' },
                { label: 'XLarge', value: 'xl' },
              ],
              defaultValue: 'm',
            },
            {
              name: 'px',
              label: 'Horizontal Padding',
              component: 'select',
              options: [
                { label: 'None', value: 'none' },
                { label: 'Small', value: 's' },
                { label: 'Medium', value: 'm' },
                { label: 'Large', value: 'l' },
                { label: 'XLarge', value: 'xl' },
              ],
              defaultValue: 'm',
            },
          ],
        },
        {
          name: 'mobile',
          label: 'Mobile Padding',
          component: 'group',
          fields: [
            {
              name: 'py',
              label: 'Vertical Padding',
              component: 'select',
              options: [
                { label: 'None', value: 'none' },
                { label: 'Small', value: 's' },
                { label: 'Medium', value: 'm' },
                { label: 'Large', value: 'l' },
                { label: 'XLarge', value: 'xl' },
              ],
              defaultValue: 's',
            },
            {
              name: 'px',
              label: 'Horizontal Padding',
              component: 'select',
              options: [
                { label: 'None', value: 'none' },
                { label: 'Small', value: 's' },
                { label: 'Medium', value: 'm' },
                { label: 'Large', value: 'l' },
                { label: 'XLarge', value: 'xl' },
              ],
              defaultValue: 's',
            },
          ],
        },
      ],
      defaultValue: {
        desktop: {
          py: 'm',
          px: 'm',
        },
        mobile: {
          py: 's',
          px: 's',
        },
      },
    },
  ],
  defaultValue: {
    ...sectionConfig.defaultValue,
    desktop: {
      container: 'content',
      maxHeight: 800,
      minHeight: 600,
    },
    mobile: {
      container: 'content',
      maxHeight: 600,
      minHeight: 500,
    },
    heroPadding: {
      desktop: {
        py: 'm',
        px: 'm',
      },
      mobile: {
        py: 's',
        px: 's',
      },
    },
  },
});

const defaultHeroValueProps = () => ({
  backgroundImage: {
    desktop: {
      src: 'https://cdn.shopify.com/s/files/1/1114/2308/files/hero-bg-desktop.jpg',
      position: 'center center',
    },
    mobile: {
      src: 'https://cdn.shopify.com/s/files/1/1114/2308/files/hero-bg-mobile.jpg',
      position: 'center center',
    },
  },
  text: {
    superheading: '',
    heading: 'The only 100% leakproof straw tumbler on the market.',
    subheading: '',
    desktopFontSize: 84,
    customDesktopFontSize: 84,
    mobileFontSize: 44,
    customMobileFontSize: 44,
    color: '#ffffff',
  },
  socialProof: {
    enabled: true,
    baseNumber: 30000,
    label: '5 Star Reviews',
    ringColor: '#ffffff',
    items: [
      {
        image: {
          src: 'https://cdn.shopify.com/s/files/1/1114/2308/files/user1.jpg',
          alt: 'User 1',
        },
      },
      {
        image: {
          src: 'https://cdn.shopify.com/s/files/1/1114/2308/files/user2.jpg',
          alt: 'User 2',
        },
      },
      {
        image: {
          src: 'https://cdn.shopify.com/s/files/1/1114/2308/files/user3.jpg',
          alt: 'User 3',
        },
      },
      {
        image: {
          src: 'https://cdn.shopify.com/s/files/1/1114/2308/files/user4.jpg',
          alt: 'User 4',
        },
      },
    ],
  },
  valueProps: {
    enabled: true,
    backgroundColor: 'rgba(0,0,0,0.8)',
    textColor: 'rgba(255,255,255,1)',
    items: [
      {
        icon: {
          src: 'https://cdn.shopify.com/s/files/1/1114/2308/files/check-icon.svg',
          alt: 'Check icon',
        },
        title: 'Innovative Design',
        description:
          'Our innovative design, has won 3 prestigious Red Dot Design Awards.',
      },
      {
        icon: {
          src: 'https://cdn.shopify.com/s/files/1/1114/2308/files/warranty-icon.svg',
          alt: 'Warranty icon',
        },
        title: 'Lifetime Warranty',
        description:
          'Our limited lifetime warranty ensures lasting quality & peace of mind.',
      },
      {
        icon: {
          src: 'https://cdn.shopify.com/s/files/1/1114/2308/files/service-icon.svg',
          alt: 'Service icon',
        },
        title: 'Flawless Customer Service',
        description:
          'The BrüGurus are dedicated to providing exceptional support via email and chat.',
      },
      {
        icon: {
          src: 'https://cdn.shopify.com/s/files/1/1114/2308/files/shipping-icon.svg',
          alt: 'Shipping icon',
        },
        title: 'Free Shipping on Us Order $75+',
        description:
          'Free shipping on orders over $75 so you can enjoy premium drinkware without added delivery costs.',
      },
    ],
  },
  section: {
    heroPadding: {
      desktop: {
        py: 'm',
        px: 'm',
      },
      mobile: {
        py: 's',
        px: 's',
      },
    },
  },
});

export const Schema = () => {
  return {
    label: 'Hero w/ Value Props',
    key: 'heroValueProps',
    fields: [
      backgroundImage(),
      text(),
      socialProof(),
      valueProps(),
      section({ sectionWithHeight }),
    ],
    defaultValue: defaultHeroValueProps(),
  };
};
